import {Button, IconButton, Modal} from "@mui/material";
import CloseIcon from "@material-ui/icons/Close";
import React, {useMemo, useState} from "react";
import styled from "styled-components";
import {usePage} from "@inertiajs/inertia-react";
import {useLanguageSwitch} from "../hooks/useLanguageSwitch";
import {getFlagEmoji} from "../utils/getFlagEmoji";
import Grid from "@material-ui/core/Grid";
import RadioButton from "./RadioBig";
import classNames from "classnames";
import {Trans} from "../utils/i18n";
import Cookies from "js-cookie";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import SearchIcon from "@material-ui/icons/Search";
import {useTimeout} from "@mui/x-data-grid/internals";
import { useShoppingCartStore } from "../stores/ShoppingCartStore";

const ModalContent = styled.div`
    background-color: white;
    width: 100%;
    max-width: min(70rem,100vw);
    max-height: 100vh;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    position: relative;
    outline: none;
    flex-wrap: wrap;
    overflow-y: scroll;
    height: 100vh;

    @media (min-width: 768px) {
        margin-top: 20vh;
        width: 90%;
        max-height: calc(100vh - 25%);
        height: auto;
    }
`;

export default function LanguageModal({ setLangModalOpen, }) {
    const {
        props: { availableCountries, languages, client, currentLanguage, currentCountry },
    } = usePage();
    const { updateLanguage } = useLanguageSwitch();
    const [selectedCountry, setSelectedCountry] = useState(currentCountry);
    const [selectedLanguage, setSelectedLanguage] = useState(currentLanguage);
    const countryFound = availableCountries.find((country) => country.code === selectedCountry);
    const store = useShoppingCartStore();

    const onSubmit = (e) => {

        if (countryFound.pivot.url) {
            window.location.href = countryFound.pivot.url;
            return;
        }

        Cookies.set("country", selectedCountry);
        setLangModalOpen(false);
        updateLanguage(selectedLanguage);
        setTimeout(() => {
            if (currentCountry !== selectedCountry) {
                store.send({
                    type: "clearCart"
                });
                window.location.reload();
            }
        }, 100);
    };

    const displayWarning = useMemo(() => {
        let country = selectedCountry.toLowerCase();
        let language = selectedLanguage.toLowerCase();
        if((country === 'gb' || country === 'us') && language === 'en') {
            return false;
        }
        if(country === 'at' && language === 'de') {
            return false;
        }
        if(country === 'ch' && ['de', 'fr', 'it'].includes(language)) {
            return false;
        }


        return country !== language;
    }, [selectedCountry, selectedLanguage,]);

    return (
        <Modal
            open={true}
            onClose={() => {
                if(!Cookies.get("country")) {
                    return;
                }
                setLangModalOpen(false);
            }}
        >

            <ModalContent>
                <IconButton aria-label="delete" onClick={() => {
                    if(!Cookies.get("country")) {
                        return;
                    }
                    setLangModalOpen(false);
                }}
                            style={{
                                position: 'absolute',
                                top: '1.5rem',
                                right: '1.5rem',
                                color: '#fff',
                            }}>
                    <CloseIcon/>
                </IconButton>
                <div>
                    <div className={'flex items-center gap-3 bg-[#131A22] text-white px-10 py-6 w-full'}>
                        <div className={'flex flex-col justify-center items-center'}>
                            {client.brand_logo && (
                                <img
                                    className={'w-auto h-12 mt-3 '}
                                    alt={'Client logo'}
                                    src={client.brand_logo}
                                />
                            )}
                        </div>
                        {
                            client.key === 'amazon' &&
                            <>
                                <div className={'hidden sm:block'}>
                                    Powered by
                                </div>
                                <div className={'hidden sm:flex items-center justify-center flex-wrap lg:flex-nowrap gap-3'}>
                                    {client.brand_logo && (
                                        <img
                                            className={'w-16 h-auto lg:w-auto lg:h-8 '}
                                            alt={'B2X'}
                                            src={
                                                "/images/logos/b2x.png"
                                            }
                                        />
                                    )}
                                    <div>&</div>
                                    {client.brand_logo && (
                                        <img
                                            className={'w-32 h-auto lg:h-10 lg:w-auto'}
                                            alt={'Genpact'}
                                            src={
                                                "/images/amazon/genpact_logo.png"
                                            }
                                        />
                                    )}
                                </div>
                            </>
                        }
                    </div>
                    <div className={'p-10 pt-6'}>
                        <h2 className={'text-2xl'}>
                            <Trans i18nKey="languageModal.title">
                                Country and language
                            </Trans>
                        </h2>
                        <p>
                            <Trans i18nKey="languageModal.description">
                                The services we offer can vary from one country to another. We therefore have to know where the service is being fulfilled. Please choose the country where you want to use our service.
                            </Trans>
                        </p>
                            {
                                availableCountries.length >= 6 &&
                                    <div className={'grid grid-cols-12'}>
                                        <div className={'col-span-3'}>
                                            <p className={'text-xl mt-9'}>
                                                <Trans i18nKey="languageModal.current">
                                                    Your current location
                                                </Trans>:
                                            </p>
                                            {
                                                ((country) => <li
                                                    className={
                                                        'cursor-pointer border-[#d49c55] bg-gray-100  shadowhover:bg-gray-50 hover:shadow p-6 py-3 inline-flex gap-3 text-lg font-bold justify-start items-center border-2 rounded-2xl w-full xs:w-auto'
                                                    }
                                                    onClick={() => setSelectedCountry(country.code)}
                                                    key={country.code}
                                                    id={'country-' + country.code}
                                                >
                                                    {getFlagEmoji(country.code)}
                                                    <span>{country.name}</span>
                                                </li>)(availableCountries.find((country) => country.code === selectedCountry))
                                            }

                                        </div>
                                        <div className={'col-span-9'}>
                                            <p className={'text-xl mt-9'}>
                                                <Trans i18nKey="languageModal.changeItHere">
                                                    Not your location? Change it here:
                                                </Trans>:</p>
                                            <Autocomplete
                                                sx={{backgroundColor: '#eeeeee'}}
                                                id="brand-box"
                                                options={availableCountries.map((country) => country.name)}
                                                renderInput={(params) => <TextField
                                                    {...params}
                                                    // label="Devices"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        sx: {
                                                            borderRadius: '0 !important',
                                                            backgroundColor: '#fff',
                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                border: 'none',
                                                                borderBottom: '3px solid #3D5166',
                                                                borderRadius: 0,
                                                            }
                                                        },
                                                        startAdornment: <SearchIcon/>,
                                                    }}
                                                    placeholder={'Country'}
                                                />}
                                                renderOption={(props, option) => {
                                                    const country = availableCountries.find((country) => country.name === option);
                                                    return (
                                                        <li {...props} key={country.code} id={'country-' + country.code}>
                                                            {getFlagEmoji(country.code)} {country.name}
                                                        </li>
                                                    );
                                                }}
                                                value={availableCountries.find((country) => country.code === selectedCountry)?.name}
                                                disableClearable
                                                // getOptionLabel={(option) => option}
                                                onChange={(event, value) => {
                                                    if (!value) {
                                                        return;
                                                    }
                                                    setSelectedCountry(availableCountries.find((country) => country.name === value)?.code);
                                                }}
                                            />
                                        </div>
                                    </div>
                                    }
                                    {
                                        availableCountries.length < 6 && <>
                                            <p className={'text-xl mt-9'}>
                                                <Trans i18nKey="languageModal.current">
                                                    Your current location
                                                </Trans>:
                                            </p>
                                            <ul className={'flex flex-row flex-wrap justify-start gap-3 p-0'}>
                                                {
                                                    availableCountries
                                                        .sort((a, b) => {
                                                            if (a.code === "US") {
                                                                return -1;
                                                            }
                                                            if (b.code === "US") {
                                                                return 1;
                                                            }
                                                            return a.name.localeCompare(b.name);
                                                        }).map((country) => (
                                                        <li
                                                            className={classNames(
                                                                'cursor-pointer hover:bg-gray-50 hover:shadow p-6 py-3 flex gap-3 text-lg font-bold justify-start items-center border-2 rounded-2xl w-full xs:w-auto',
                                                                {
                                                                    'border-[#d49c55] bg-gray-100  shadow': selectedCountry === country.code,
                                                                })
                                                            }
                                                            onClick={() => setSelectedCountry(country.code)}
                                                            key={country.code}
                                                            id={'country-' + country.code}
                                                        >
                                                            {getFlagEmoji(country.code)}
                                                            <span>{country.name}</span>
                                                        </li>
                                                    ))
                                                }
                                            </ul>
                                        </>
                                    }
                                    {
                                        countryFound?.pivot?.url && (
                                            <>
                                                <div className={'bg-red-300/50 py-3 px-9 rounded-xl mb-6 text-center'}>
                                                    <h2>
                                                        <Trans i18nKey="languageModal.warningh2">
                                                            Is
                                                            “{{countryName: availableCountries.find((country) => country.code === selectedCountry)?.name}}”
                                                            the right country?
                                                        </Trans>
                                                    </h2>
                                                    <p>
                                                        <Trans i18nKey="languageModal.redirectWarning">
                                                            You will be redirected to the page dedicated to this country.
                                                        </Trans>
                                                    </p>
                                                    <p>
                                                        <Trans i18nKey="languageModal.warning2">
                                                            Are you sure you want to proceed?
                                                        </Trans>
                                                    </p>
                                                </div>
                                            </>
                                        )
                                    }

                                    {
                                        !countryFound?.pivot?.url && (
                                            <>
                                                {
                                                    languages.length >= 6 &&
                                                    <div className={'grid grid-cols-12 my-9'}>
                                                        <div className={'col-span-3'}>
                                                            <p className={'text-xl mt-6 '}>
                                                                <Trans i18nKey="languageModal.currentLang">
                                                                    Your current language
                                                                </Trans>
                                                                :
                                                            </p>
                                                            {
                                                                ((language) => <li
                                                                    className={classNames(
                                                                        'cursor-pointer hover:bg-gray-50 hover:shadow p-6 py-3 inline-flex gap-3 text-lg font-bold justify-start items-center border-2 rounded-2xl w-full xs:w-auto',
                                                                        {
                                                                            'border-[#d49c55] bg-gray-100  shadow': selectedLanguage === language.code,
                                                                        })
                                                                    }
                                                                    onClick={() => setSelectedLanguage(language.code)}
                                                                    key={language.code}
                                                                    id={'language-' + language.code}
                                                                >
                                                                    {getFlagEmoji(language.code)}
                                                                    <span>{language.selfname}</span>
                                                                </li>
                                                                )(languages.find((language) => language.code === selectedLanguage))
                                                            }
                                                        </div>
                                                        <div className={'col-span-9'}>
                                                            <p className={'text-xl mt-6 '}>
                                                                <Trans i18nKey="languageModal.changeLang">
                                                                    Not your language? Change it here
                                                                </Trans>
                                                                :
                                                            </p>
                                                            <Autocomplete
                                                                sx={{backgroundColor: '#eeeeee'}}
                                                                id="brand-box"
                                                                options={languages.map((language) => language.selfname)}
                                                                renderInput={(params) => <TextField
                                                                    {...params}
                                                                    // label="Devices"
                                                                    InputProps={{
                                                                        ...params.InputProps,
                                                                        sx: {
                                                                            borderRadius: '0 !important',
                                                                            backgroundColor: '#fff',
                                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                                border: 'none',
                                                                                borderBottom: '3px solid #3D5166',
                                                                                borderRadius: 0,
                                                                            }
                                                                        },
                                                                        startAdornment: <SearchIcon/>,
                                                                    }}
                                                                    placeholder={'Language'}
                                                                />}
                                                                value={languages.find((language) => language.code === selectedLanguage)?.selfname}
                                                                disableClearable
                                                                // getOptionLabel={(option) => option}
                                                                onChange={(event, value) => {
                                                                    if (!value) {
                                                                        return;
                                                                    }
                                                                    setSelectedLanguage(languages.find((language) => language.selfname === value)?.code);
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                }
                                                {
                                                    languages.length < 6 &&
                                                        <div>
                                                            <p className={'text-xl mt-9 '}>
                                                                <Trans i18nKey="languageModal.currentLang">
                                                                    Your current language
                                                                </Trans>
                                                                :
                                                            </p>
                                                            <ul className={'flex flex-row flex-wrap justify-start gap-3 p-0'}>
                                                                {
                                                                    languages
                                                                        .sort((a, b) => {
                                                                            if (a.code === "en") {
                                                                                return -1;
                                                                            }
                                                                            if (b.code === "en") {
                                                                                return 1;
                                                                            }
                                                                            return a.selfname.localeCompare(b.selfname);
                                                                        })
                                                                        .map((language) =>
                                                                            <li
                                                                                className={classNames(
                                                                                    'cursor-pointer hover:bg-gray-50 hover:shadow p-6 py-3 flex gap-3 text-lg font-bold justify-start items-center border-2 rounded-2xl w-full xs:w-auto',
                                                                                    {
                                                                                        'border-[#d49c55] bg-gray-100  shadow': selectedLanguage === language.code,
                                                                                    })
                                                                                }
                                                                                onClick={() => setSelectedLanguage(language.code)}
                                                                                key={language.code}
                                                                                id={'language-' + language.code}
                                                                            >
                                                                                {getFlagEmoji(language.code)}
                                                                                <span>{language.selfname}</span>
                                                                            </li>
                                                                        )
                                                                }
                                                            </ul>
                                                        </div>
                                                        }
                                                        {
                                                            displayWarning && (
                                                                <div
                                                                    className={'bg-red-300/50 py-3 px-9 rounded-xl mb-6 text-center'}>
                                                                    <h2>
                                                                        <Trans i18nKey="languageModal.warningh2">
                                                                            Is
                                                                            “{{countryName: availableCountries.find((country) => country.code === selectedCountry)?.name}}”
                                                                            the right country?
                                                                        </Trans>
                                                                    </h2>
                                                                    <p>
                                                                        <Trans i18nKey="languageModal.warning1">
                                                                            Please be aware that the country can have an
                                                                            impact on our
                                                                            offerings.
                                                                        </Trans>
                                                                    </p>
                                                                    <p>
                                                                        <Trans i18nKey="languageModal.warning2">
                                                                            Are you sure you want to proceed?
                                                                        </Trans>
                                                                    </p>
                                                                </div>
                                                            )
                                                        }
                                                    </>
                                                    )
                                                }
                                                <div className={'w-full flex justify-end'}>
                                                    <Button
                                                        variant={'contained'}
                                                        color={'primary'}
                                                        onClick={onSubmit}
                                                        id={'languageModalSubmit'}
                                        >
                                            <Trans i18nKey="profile.save">Save</Trans>
                                        </Button>
                                    </div>
                                </div>
                                </div>
                                </ModalContent>

        </Modal>
    );
}
